import { useToggle, upperFirst } from '@mantine/hooks'
import { useForm } from '@mantine/form'

import { useUser, useSupabaseClient } from '@supabase/auth-helpers-react'

import { useNavigate } from 'react-router-dom'
import { useState } from 'react'

import {
    TextInput,
    PasswordInput,
    PinInput,
    Button,
    Anchor,
    Stack,
} from '@mantine/core'

import { IconCircleCheckFilled } from '@tabler/icons-react'

export default function AuthenticationFormOTP({ onBack, passwordUpdate }) {
    const [step, setStep] = useState('email')

    const [isLoading, setIsLoading] = useState(false)

    const supabaseClient = useSupabaseClient()
    const user = useUser()
    const navigate = useNavigate()

    const form = useForm({
        initialValues: {
            email: '',
        },

        validate: {
            email: (val) => (/^\S+@\S+$/.test(val) ? null : 'Invalid email'),
        },
    })

    const formOnSubmit = form.onSubmit(async ({ email, password }) => {
        setIsLoading(true)

        const { error } = await supabaseClient.auth.signInWithOtp({
            email,
        })

        if (error) {
            form.setFieldError('email', "Ce compte n'existe pas")
        } else {
            setStep('code')
        }

        setIsLoading(false)
    })

    // Code verification

    const formCode = useForm({
        initialValues: {
            code: '',
        },
    })

    const formCodeOnSubmit = formCode.onSubmit(async ({ code }) => {
        setIsLoading(true)

        const { error } = await supabaseClient.auth.verifyOtp({
            email: form.values.email,
            token: code,
            type: 'email',
        })

        if (error) {
            formCode.setFieldError(
                'email',
                'Code invalide, veuillez demander un nouveau code'
            )

            setStep('email')
        } else {
            setStep('password')
        }

        setIsLoading(false)
    })

    // Password update
    const formPassword = useForm({
        initialValues: {
            password: '',
            password_confirmation: '',
        },

        validate: {
            password: (val) =>
                val.length >= 10 ? null : 'Le mot de passe est trop court',
            password_confirmation: (val) =>
                val === formPassword.values.password
                    ? null
                    : 'Les mots de passe ne correspondent pas',
        },
    })

    const formPasswordOnSubmit = formPassword.onSubmit(async ({ password }) => {
        setIsLoading(true)

        const { error } = await supabaseClient.auth.updateUser({
            password,
        })

        if (error) {
            formPassword.setFieldError(
                'password',
                'Erreur lors de la mise à jour du mot de passe'
            )
        } else {
            setStep('success')
        }

        setIsLoading(false)
    })

    return (
        <>
            {step === 'email' && (
                <form
                    className="bg-white border p-10 rounded-sm"
                    onSubmit={formOnSubmit}
                >
                    <Stack>
                        <span className="font-medium text-lg text-center">
                            Créer ou modifier votre mot de passe
                        </span>

                        <span className="text-center text-sm">
                            Pour créer ou modifier votre mot de passe,
                            <br />
                            vous devez recevoir un code par email.
                        </span>

                        <TextInput
                            required
                            label="Email"
                            placeholder="email@exemple.fr"
                            withAsterisk={false}
                            value={form.values.email}
                            onChange={(event) =>
                                form.setFieldValue(
                                    'email',
                                    event.currentTarget.value
                                )
                            }
                            error={form.errors.email}
                            radius="md"
                        />

                        <Button type="submit" fullWidth loading={isLoading}>
                            Recevoir un code par email
                        </Button>

                        <div className="text-center mx-auto">
                            <Anchor onClick={onBack} size="sm">
                                Se connecter avec un mot de passe
                            </Anchor>
                        </div>
                    </Stack>
                </form>
            )}

            {step === 'code' && (
                <form
                    className="bg-white border p-10 rounded-sm"
                    onSubmit={formCodeOnSubmit}
                >
                    <Stack>
                        <span className="font-medium text-lg text-center">
                            Code
                        </span>

                        <span className="text-center text-sm">
                            Indiquez le code reçu par email
                        </span>

                        <PinInput
                            className="mx-auto"
                            type="number"
                            length={6}
                            {...formCode.getInputProps('code')}
                        />

                        <Button type="submit" fullWidth loading={isLoading}>
                            Valider le code
                        </Button>

                        <div className="text-center mx-auto">
                            <Anchor onClick={() => setStep('email')} size="sm">
                                Revenir à l'étape précédente
                            </Anchor>
                        </div>
                    </Stack>
                </form>
            )}

            {step === 'password' && (
                <form
                    className="bg-white border p-10 rounded-sm"
                    onSubmit={formPasswordOnSubmit}
                >
                    <Stack>
                        <span className="font-medium text-lg text-center">
                            Nouveau mot de passe
                        </span>

                        <div className="flex flex-col gap-1">
                            <span className="text-sm font-medium">Email</span>
                            <span>{form.values.email}</span>
                        </div>

                        <PasswordInput
                            label="Mot de passe"
                            description="10 caractères minimum"
                            {...formPassword.getInputProps('password')}
                        />

                        <PasswordInput
                            label="Confirmation de mot de passe"
                            {...formPassword.getInputProps(
                                'password_confirmation'
                            )}
                        />

                        <Button type="submit" fullWidth loading={isLoading}>
                            Créer mon nouveau mot de passe
                        </Button>

                        <div className="text-center mx-auto">
                            <Anchor onClick={() => setStep('email')} size="sm">
                                Revenir au début
                            </Anchor>
                        </div>
                    </Stack>
                </form>
            )}

            {step === 'success' && (
                <div className="bg-white border p-10 rounded-sm flex flex-col items-center gap-4">
                    <IconCircleCheckFilled
                        className="mx-auto text-green-500"
                        size={50}
                    />

                    <span className="text-green-500 font-medium">
                        Mot de passe modifié
                    </span>

                    <Button
                        className="mt-10"
                        fullWidth
                        onClick={() => navigate('/')}
                    >
                        Accéder au backoffice
                    </Button>
                </div>
            )}
        </>
    )
}
